import { useDispatch, useSelector } from '@redux';
import { getAddress } from '@redux/config/actions';
import { Col, Form, FormInstance } from 'antd';
import { useEffect, useState } from 'react';
import SelectCustom from './SelectCustom';
import { DynamicObjectType } from 'types/commons';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

interface VNCitySelectorTypes {
  hasWard?: boolean;
  hasDistrict?: boolean;
  isRequired?: boolean;
  form: FormInstance;
}

export default function VNCitySelector({
  form,
  hasWard = true,
  hasDistrict = true,
  isRequired = true,
}: VNCitySelectorTypes) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cities = useSelector(
    (state) => state.config.address,
  ) as DynamicObjectType[];

  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);

  const selectCity = (e) => {
    const selectedCity = cities?.find((city) => city.id === e);
    setDistricts(selectedCity.districts);
    form.setFieldsValue({
      districtId: null,
      wardId: null,
      province: selectedCity.name,
    });
    return e;
  };

  const selectDistrict = (e) => {
    setWards(districts.find((district) => district.id === e)?.wards);
    form.setFieldsValue({
      wardId: null,
      district: districts.find((district) => district.id === e)?.name,
    });
    return e;
  };

  const selectWard = (e) => {
    form.setFieldsValue({
      ward: wards.find((ward) => ward.id === e)?.name,
    });
    return e;
  };

  useEffect(() => {
    dispatch(getAddress());
  }, []);

  useEffect(() => {
    const selectedCity = cities?.find(
      (city) => city.id === get(form.getFieldsValue(), 'provinceId'),
    );
    setDistricts(selectedCity?.districts);
    setWards(
      selectedCity?.districts.find(
        (district) => district.id === get(form.getFieldsValue(), `districtId`),
      )?.wards,
    );
  }, [cities, form]);

  return (
    <>
      <Col md={12} sm={24} xs={24}>
        <SelectCustom
          source="provinceId"
          resourceData={cities}
          titleProp="name"
          valueProp="id"
          formOptions={{ getValueFromEvent: selectCity }}
          header={t('mores.locations.address.city.label')}
          placeholder={t('mores.locations.address.city.placeholder')}
          required={isRequired}
        />
      </Col>
      {hasDistrict && (
        <Col md={12} sm={24} xs={24}>
          <SelectCustom
            source="districtId"
            resourceData={districts}
            titleProp="name"
            valueProp="id"
            header={t('mores.locations.address.district.label')}
            formOptions={{ getValueFromEvent: selectDistrict }}
            placeholder={t('mores.locations.address.district.placeholder')}
            required={isRequired}
          />
        </Col>
      )}
      {hasWard && (
        <Col md={12} sm={24} xs={24}>
          <SelectCustom
            source="wardId"
            resourceData={wards}
            titleProp="name"
            valueProp="id"
            header={t('mores.locations.address.ward.label')}
            placeholder={t('mores.locations.address.ward.placeholder')}
            required={isRequired}
            formOptions={{ getValueFromEvent: selectWard }}
          />
        </Col>
      )}
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, curValues) => {
          const selectedCity = cities?.find(
            (city) => city.id === get(curValues, 'provinceId'),
          );
          if (
            !districts?.length &&
            get(curValues, 'provinceId') &&
            selectedCity?.districts
          ) {
            setDistricts(selectedCity.districts);
          }
          if (
            get(prevValues, 'provinceId') !== get(curValues, 'provinceId') &&
            selectedCity?.districts
          ) {
            setDistricts(selectedCity?.districts);
          }
          if (
            !wards?.length &&
            get(curValues, 'districtId') &&
            selectedCity?.districts
          ) {
            setWards(
              selectedCity?.districts?.find(
                (district) => district.id === get(curValues, 'districtId'),
              )?.wards,
            );
          }
          if (
            get(prevValues, 'districtId') !== get(curValues, 'districtId') &&
            selectedCity?.districts
          ) {
            setWards(
              selectedCity?.districts?.find(
                (district) => district.id === get(curValues, 'districtId'),
              )?.wards,
            );
          }
          return true;
        }}
      >
        {() => null}
      </Form.Item>
      <Form.Item name="province" hidden noStyle />
      <Form.Item name="district" hidden noStyle />
      <Form.Item name="ward" hidden noStyle />
    </>
  );
}
